var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/arrow-back';
import styles from './breadcrumbs-item.styles';
/**
 * @slot before - The component before content
 * @slot default - The component content
 * @slot after - The component after content
 * @slot separator - The component separator
 * @slot custom-link - The component's custom link slot
 * @slot showmore - The component showmore content
 * @csspart base - The component's base wrapper.
 * @csspart mobile-action - The component's wrapper for mobile action.
 * @csspart icon - The component's icon `cat-icon-arrow-back`.
 * @csspart text - The component's text.
 * @csspart part-default - The component's default slot.
 * @csspart separator-wrapper - The component's separator wrapper.
 * @csspart part-separator - The component's separator.
 * @csspart default-separator - The component's default-separator.
 * @csspart link - The component's link element.
 * @csspart before-wrapper - The component's before wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart link-text - The component's link-text.
 * @csspart after-wrapper - The component's after wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart label-button - The component's label-button wrapper.
 * @csspart show-more-wrapper - The component's show-more wrapper.
 * @csspart show-more - The component's show-more slot.
 * @csspart separator-wrapper - The component's separator wrapper.
 * @csspart separator - The component's separator slot.
 * @csspart default-separator - The component's default-separator.
 * @csspart part-custom-link - The component's custom link slot

 */
export class BlocksBreadcrumbsItem extends BlocksElement {
    constructor() {
        super(...arguments);
        this.slots = new SlotController(this, null, 'before', 'after', 'separator', 'custom-link', 'showmore');
    }
    static { this.styles = styles; }
    /**
     * prevent click on separator icon
     * @param event Event
     */
    stopEventPropagation(event) {
        event.stopPropagation();
    }
    get hasBeforeSlot() {
        return this.slots.hasSlotted('before');
    }
    get hasAfterSlot() {
        return this.slots.hasSlotted('after');
    }
    get hasCustomLinkSlot() {
        return this.slots.hasSlotted('custom-link');
    }
    get hasShowMoreSlot() {
        return this.slots.hasSlotted('showmore');
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-breadcrumbs__item', {
            'cat-c-breadcrumbs__item--mobile': this.isMobile === true,
            'cat-c-breadcrumbs__item--active': this.isActive,
            'cat-c-breadcrumbs--has-before': this.hasBeforeSlot,
            'cat-c-breadcrumbs--has-after': this.hasAfterSlot,
            'cat-c-breadcrumbs--has-showmore': this.hasShowMoreSlot,
            'cat-c-breadcrumbs--not-is-last': !this.isLast,
            'cat-c-breadcrumbs--has-no-showmore': !this.href && !this.hasCustomLinkSlot && !this.hasShowMoreSlot
        });
        return html `
      <div part="base" class="${componentClassNames}">
        ${this.isMobile
            ? html `
              <div part="mobile-action" class="cat-c-breadcrumbs__item--mobile--action" tabindex="0" role=${this.href ? 'link' : 'button'}>
                <cat-icon-arrow-back part="icon" exportparts=${this.getExportParts('arrow-back', 'icon')}></cat-icon-arrow-back>
                <span part="text" class="cat-c-breadcrumbs__item--text">
                  <slot part="part-default"></slot>
                </span>
              </div>
              <div
                part="separator-wrapper"
                class="cat-c-breadcrumbs__separator breadcrumbs-separator-mobile"
                inert="${true}"
                @click=${this.stopEventPropagation}
              >
                <slot part="part-separator" name="separator">
                  <span part="default-separator" class="default-seperator">/</span>
                </slot>
              </div>
            `
            : html ` ${this.href
                ? html `
                    <a
                      part="link"
                      class="cat-c-breadcrumbs__link"
                      href=${ifDefined(this.href)}
                      target=${ifDefined(this.target)}
                      aria-current=${ifDefined(this.isActive ? 'page' : undefined)}
                    >
                      <div part="before-wrapper" class="cat-c-breadcrumbs__before">
                        <slot part="part-before" name="before"></slot>
                      </div>
                      <span part="link-text" class="cat-c-breadcrumbs__link--text">
                        <slot part="part-default"></slot>
                      </span>
                      <div part="after-wrapper" class="cat-c-breadcrumbs__after">
                        <slot part="part-after" name="after"></slot>
                      </div>
                    </a>
                  `
                : this.hasCustomLinkSlot
                    ? html ` <div part="link" class="cat-c-breadcrumbs__link">
                      <span part="link-text" class="cat-c-breadcrumbs__link--text">
                        <slot part="part-custom-link" name="custom-link"></slot>
                      </span>
                    </div>`
                    : ''}

              <button
                part="label-button"
                type="button"
                class="cat-c-breadcrumbs__item--label--button"
                tabindex=${ifDefined(this.isLoading ? '-1' : undefined)}
                aria-current=${ifDefined(this.isActive ? 'page' : undefined)}
              >
                <div part="before-wrapper" class="cat-c-breadcrumbs__before">
                  <slot part="part-before" name="before"></slot>
                </div>
                <slot part="part-default"></slot>
                <div part="after-wrapper" class="cat-c-breadcrumbs__after">
                  <slot part="part-after" name="after"></slot>
                </div>
              </button>
              <div part="show-more-wrapper" class="cat-c-breadcrumbs__showmore">
                <slot part="show-more" name="showmore"></slot>
              </div>
              <div
                part="separator-wrapper"
                class="cat-c-breadcrumbs__separator breadcrumbs-separator-lg"
                aria-hidden="true"
                @click=${this.stopEventPropagation}
              >
                <slot part="part-separator" name="separator">
                  <span part="default-separator" class="default-seperator">/</span>
                </slot>
              </div>`}
      </div>
    `;
    }
}
__decorate([
    property({ type: String })
], BlocksBreadcrumbsItem.prototype, "href", void 0);
__decorate([
    property({ type: Boolean })
], BlocksBreadcrumbsItem.prototype, "isLoading", void 0);
__decorate([
    property({ type: String })
], BlocksBreadcrumbsItem.prototype, "target", void 0);
__decorate([
    property({ type: Boolean })
], BlocksBreadcrumbsItem.prototype, "isLast", void 0);
__decorate([
    property({ type: Boolean })
], BlocksBreadcrumbsItem.prototype, "isMobile", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksBreadcrumbsItem.prototype, "isActive", void 0);
if (customElements.get('cat-breadcrumbs-item') === undefined) {
    customElements.define('cat-breadcrumbs-item', BlocksBreadcrumbsItem);
}
